'use client'

import { showNotification } from 'components/Notification'
import { useSearchState } from 'hooks/useSearchState'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { ComponentType, useCallback, useEffect, useState } from 'react'

import { API_ROUTES, SEARCH_PARAMS } from '@/constants'
import { getPathname, usePathname } from '@/i18n-routing'

import type { TCheckInboxDialogProps } from '../../../../auth/CheckInboxDialog'
import type { TResetLinkExpiredDialogProps } from '../../../../auth/ResetLinkExpiredDialog'
import type { TResetPasswordDialogProps } from '../../../../auth/ResetPasswordDialog'
import type { TCancelSubscriptionSuccessDialogProps } from './CancelSubscriptionSuccessDialog'

export const LazyCheckInboxDialog = ({
	translations,
}: {
	translations: TCheckInboxDialogProps['translations']
}) => {
	const [isOpen, setIsOpen] = useSearchState(
		SEARCH_PARAMS.modals.key,
		SEARCH_PARAMS.modals.recoverPasswordConfirmation,
	)

	const [Dialog, setDialog] =
		useState<ComponentType<TCheckInboxDialogProps> | null>(null)

	useEffect(() => {
		const loadDialog = async () => {
			const mod = await import('../../../../auth/CheckInboxDialog')
			setDialog(() => mod.CheckInboxDialog)
		}
		if (isOpen && !Dialog) {
			void loadDialog()
		}
	}, [Dialog, isOpen])

	return Dialog ? (
		<Dialog isOpen={isOpen} setIsOpen={setIsOpen} translations={translations} />
	) : null
}

export const LazyResetPasswordDialog = ({
	translations,
}: {
	translations: TResetPasswordDialogProps['translations']
}) => {
	const [isOpen, setIsOpen] = useSearchState(
		SEARCH_PARAMS.modals.key,
		SEARCH_PARAMS.modals.resetPassword,
	)

	const [Dialog, setDialog] =
		useState<ComponentType<TResetPasswordDialogProps> | null>(null)

	useEffect(() => {
		const loadDialog = async () => {
			const mod = await import('../../../../auth/ResetPasswordDialog')
			setDialog(() => mod.ResetPasswordDialog)
		}
		if (isOpen && !Dialog) {
			void loadDialog()
		}
	}, [Dialog, isOpen])

	return Dialog ? (
		<Dialog isOpen={isOpen} setIsOpen={setIsOpen} translations={translations} />
	) : null
}

export const LazyResetLinkExpiredDialog = ({
	translations,
}: {
	translations: TResetLinkExpiredDialogProps['translations']
}) => {
	const [isOpen, setIsOpen] = useSearchState(
		SEARCH_PARAMS.modals.key,
		SEARCH_PARAMS.modals.resetLinkExpired,
	)

	const [Dialog, setDialog] =
		useState<ComponentType<TResetLinkExpiredDialogProps> | null>(null)

	useEffect(() => {
		const loadDialog = async () => {
			const mod = await import('../../../../auth/ResetLinkExpiredDialog')
			setDialog(() => mod.ResetLinkExpiredDialog)
		}
		if (isOpen && !Dialog) {
			void loadDialog()
		}
	}, [Dialog, isOpen])

	return Dialog ? (
		<Dialog isOpen={isOpen} setIsOpen={setIsOpen} translations={translations} />
	) : null
}

export const LazyCancelSubscriptionSuccessDialog = ({
	translations,
}: {
	translations: TCancelSubscriptionSuccessDialogProps['translations']
}) => {
	const PARAM_CANCEL_SUBSCRIPTION_REQUEST_ID = 'cancelSubscriptionRequestId'

	const [isOpen, setIsOpen] = useState<boolean>(false)
	const [Dialog, setDialog] =
		useState<ComponentType<TCancelSubscriptionSuccessDialogProps> | null>(null)

	const params = useSearchParams()
	const cancelSubscriptionRequestId = params.get(
		PARAM_CANCEL_SUBSCRIPTION_REQUEST_ID,
	)
	const locale = useLocale()
	const pathname = usePathname()
	const pathToRedirect = getPathname({ locale, href: pathname })
	const t = useTranslations()

	const removeSubscriptionIdFromParams = useCallback(() => {
		const url = new URL(window.location.href)
		url.searchParams.delete(PARAM_CANCEL_SUBSCRIPTION_REQUEST_ID)
		window.history.replaceState(null, '', pathToRedirect)
	}, [pathToRedirect])

	const requestCancelSubscription = useCallback(async () => {
		if (!cancelSubscriptionRequestId) {
			return
		}

		try {
			const { getApiClient } = await import('@/api')
			await getApiClient().post(
				API_ROUTES.subscription.common.cancel(cancelSubscriptionRequestId),
			)
			const mod = await import('./CancelSubscriptionSuccessDialog')
			setDialog(() => mod.CancelSubscriptionSuccessDialog)
			setIsOpen(true)
		} catch (err) {
			const axios = await import('axios')
			if (axios.isAxiosError(err)) {
				const errorMsg = err.response?.data?.message
				if (errorMsg === 'api.errors.cancelSubscription.requestExpired') {
					const errorMessage = t(
						'public__cancel_subscription__link_expired_message',
					)

					void showNotification(errorMessage, { type: 'warn' })
				} else {
					void showNotification(errorMsg, { type: 'error' })
				}
			}
			removeSubscriptionIdFromParams()
		}
	}, [cancelSubscriptionRequestId, removeSubscriptionIdFromParams, t])

	const handleClose = useCallback(() => {
		setIsOpen(false)
		removeSubscriptionIdFromParams()
	}, [removeSubscriptionIdFromParams])

	useEffect(() => {
		void requestCancelSubscription()
	}, [requestCancelSubscription])

	if (!Dialog || !isOpen) {
		return null
	}

	return (
		<Dialog isOpen={isOpen} onClose={handleClose} translations={translations} />
	)
}
